import React from "react";
import { v4 as uuid } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../../Store/actions";
import styles from "./styles";
import { useTranslation } from "react-i18next";

import { Box } from "@mui/material/";

import {
    getCompressedImage,
    getCompressedImageWithThumbnail,
} from "../BrowserCamera/components/helper";
import { useToaster, useAssetTypeImageTypes } from "../../hooks";
import useImageTypes from "../PhotoList/useImageTypes";
import useImageType from "./useImageType";
import { logToSentry } from "../../../config/Sentry";

const UPLOAD_SEC = 5;

const NativeCamera = ({ step, imgIndex, altImageType, onImageCapture }) => {
    const dispatch = useDispatch();
    const toast = useToaster();
    const { t } = useTranslation();

    const { geoLocation, assetDetails } = useSelector((state) => state);
    const {
        activeStep,
        steps,
        photos,
        stepsCompleted,
        assetTypeId,
        exclusionReasonTypeId,
    } = assetDetails;

    const assetImageTypes = useAssetTypeImageTypes(assetTypeId);
    const imageTypes = useImageTypes(assetImageTypes, exclusionReasonTypeId);
    const [imageTypeId, ___] = useImageType(imgIndex, imageTypes);

    const getImageTypeName = (imgId) =>
        imageTypes.find((item) => item.id === imgId);

    const isUploaded = (date) => {
        const now = new Date();
        const diff = now - date; // Calculate the difference in milliseconds
        const seconds = diff / 1000; // Convert milliseconds to seconds
        return UPLOAD_SEC < seconds; // Check the difference is less than given seconds
    };

    const handleCapture = async (event) => {
        try {
            const file = event.target.files[0];
            const reader = new FileReader();

            reader.onload = async () => {
                const dataUrl = reader.result;

                // to support altAsset component
                let imageUrl = "";
                let thumbnailUrl = "";

                if (onImageCapture) {
                    const img = await getCompressedImageWithThumbnail(dataUrl);
                    imageUrl = img.imageUrl;
                    thumbnailUrl = img.thumbnailUrl;
                } else imageUrl = await getCompressedImage(dataUrl);

                const imgTypeId = altImageType ? altImageType : imageTypeId;
                const imageState = {
                    id: uuid(),
                    imageType: imgTypeId, // suport altAset component
                    createdDate: new Date().toISOString(),
                    image: imageUrl,
                    thumbnail: thumbnailUrl,
                    latitude: geoLocation.coords.latitude,
                    longitude: geoLocation.coords.longitude,
                    uploaded: isUploaded(file.lastModified),
                };

                const updPhotos = [...photos];
                if (updPhotos[imgIndex]) updPhotos[imgIndex] = imageState;
                else updPhotos.push(imageState);

                const updStepsCompleted = [...stepsCompleted];
                updStepsCompleted[activeStep] = true;

                /** Updating steps with the image Id name */
                const labelName = getImageTypeName(imgTypeId);
                const updSteps = [...steps];

                /** Check if step is present
                 * 1. For alt asset not activeStep and label name so we can skip it
                 */
                if (activeStep) {
                    updSteps[activeStep].label = labelName
                        ? labelName.name
                        : updSteps[activeStep].label;
                }

                // to support altAsset component
                if (onImageCapture) {
                    onImageCapture(imageState);
                } else
                    dispatch(
                        actions.assetDetails.create.set({
                            photos: updPhotos,
                            stepsCompleted: updStepsCompleted,
                            // incremenet to the next step
                            activeStep:
                                step + 1 > stepsCompleted.length
                                    ? step
                                    : step + 1,
                        })
                    );

                // need to reset target value, otherwise same file cannot be uploaded
                event.target.value = null;
            };

            reader.readAsDataURL(file);
        } catch (error) {
            console.log(error);
            logToSentry("NativeCamera:handleCapture", {
                error,
                errStr: error.toString(),
            });
            toast.error(t("errors.checkAsset"));
        }
    };

    return (
        <Box sx={styles.wrapCamera}>
            <input
                id={`camera-file-upload-${imgIndex}`}
                data-testid="camera-file-input"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleCapture}
                style={styles.camera}
            />
        </Box>
    );
};

export default NativeCamera;
